import React, { useMemo } from 'react';
import { Box, Card, CardContent, CardHeader, Divider, Grid, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ConversionFamily, PeriodConversion, StaffPeriods } from '../types';
import { CONVERSION_TRACK_COLOR, SEGMENT_COLORS, SEGMENT_LABELS, SEGMENT_ORDER, countSegments, monthlySegments, topSegment } from '../utils/conversionSegments';
import { formatDecimal, monthLabel, periodLabel } from '../utils/metrics';
import { ChartLegend } from './ChartLegend';
import { ConversionClientsTable } from './ConversionClientsTable';
import { ConversionSegmentMeter } from './ConversionSegmentMeter';
import { EmptyState } from './EmptyState';

const CONVERTED_COLOR = '#2563EB';
const NOT_CONVERTED_COLOR = '#D1D5DB';

/**
 * A segment's corner radii: only the topmost non-zero segment of a month gets the rounded top. Recharts merges a Cell's
 * props into its bar's rectangle, which takes per-corner radii, but Cell's SVG prop types only allow a single number.
 */
const segmentRadius = (top: boolean) => (top ? [4, 4, 0, 0] : [0, 0, 0, 0]) as unknown as number;

export interface ConversionCardProps {
  conversion: ConversionFamily | null;
  periods: StaffPeriods;
  sourceCreatedDate: string;
}

interface Point {
  label: string;
  converted: number;
  notConverted: number;
  finished: number;
  dropped: number;
  expired: number;
  ratePct: number | null;
}

const pct = (value: number | null): string => (value === null ? 'n/a' : `${formatDecimal(value)}%`);

interface ConversionTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: Point }>;
  /** Adds one line per non-zero segment; only the three-segment chart passes it. */
  segments?: boolean;
}

const ConversionTooltip: React.FC<ConversionTooltipProps> = ({ active, payload, segments }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <Paper elevation={6} sx={{ p: 1.5, borderRadius: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {point.label}
      </Typography>
      <Typography variant="body2">
        {point.converted} of {point.expired} converted
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {point.expired === 0 ? 'No first packages expired' : pct(point.ratePct)}
      </Typography>
      {segments
        ? SEGMENT_ORDER.filter((key) => point[key] > 0).map((key) => (
            <Typography key={key} variant="body2">
              {`${point[key]} ${SEGMENT_LABELS[key].toLowerCase()}`}
            </Typography>
          ))
        : null}
    </Paper>
  );
};

interface MeterProps {
  label: string;
  period: PeriodConversion;
  muted?: boolean;
}

const Meter: React.FC<MeterProps> = ({ label, period, muted }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography variant="body2" sx={{ fontWeight: muted ? 400 : 600, color: muted ? 'text.secondary' : 'text.primary' }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {period.converted} of {period.expired}
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={period.ratePct ?? 0}
      aria-label={`${label} conversion rate`}
      sx={{
        mt: 0.75,
        height: 8,
        borderRadius: 4,
        bgcolor: CONVERSION_TRACK_COLOR,
        '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: CONVERTED_COLOR, opacity: muted ? 0.55 : 1 },
      }}
    />
    <Typography sx={{ mt: 0.75, fontSize: muted ? 16 : 22, fontWeight: muted ? 600 : 700, color: muted ? 'text.secondary' : 'text.primary' }}>
      {pct(period.ratePct)}
    </Typography>
  </Box>
);

/**
 * First-programme conversion by expiry month (stacked bars) with period meters and the all-time rate. With a client
 * list the non-converted packages split into finished and dropped, and the clients are listed under the chart.
 */
export const ConversionCard: React.FC<ConversionCardProps> = ({ conversion, periods, sourceCreatedDate }) => {
  const hasClients = (conversion?.clients.length ?? 0) > 0;
  const data = useMemo<Point[]>(
    () =>
      (conversion ? monthlySegments(conversion) : []).map((m) => ({
        label: monthLabel(m.month),
        converted: m.converted,
        notConverted: Math.max(0, m.expired - m.converted),
        finished: m.finished,
        dropped: m.dropped,
        expired: m.expired,
        ratePct: m.ratePct,
      })),
    [conversion]
  );

  return (
    <Card>
      <CardHeader
        title="First programme conversion"
        subheader={
          hasClients
            ? 'Clients whose first Circle of Care package expired that month: who bought another package, who finished without buying, and who dropped off'
            : 'Clients whose first Circle of Care package expired that month, and how many went on to buy another package'
        }
        sx={{ pb: 1 }}
      />
      <Box sx={{ px: 2.5, pb: 1.5 }}>
        <ChartLegend
          keys={
            hasClients
              ? SEGMENT_ORDER.map((key) => ({ label: SEGMENT_LABELS[key], color: SEGMENT_COLORS[key] }))
              : [
                  { label: 'Converted', color: CONVERTED_COLOR },
                  { label: 'Did not convert', color: NOT_CONVERTED_COLOR },
                ]
          }
        />
      </Box>
      <CardContent sx={{ pt: 0 }}>
        {conversion ? (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Box sx={{ width: '100%', height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke="#E5E7EB" />
                      <XAxis dataKey="label" interval={0} tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={{ stroke: '#D1D5DB' }} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                      <Tooltip content={<ConversionTooltip segments={hasClients} />} cursor={{ fill: 'rgba(37, 99, 235, 0.08)' }} />
                      {hasClients
                        ? SEGMENT_ORDER.map((key) => (
                            <Bar
                              key={key}
                              dataKey={key}
                              stackId="a"
                              fill={SEGMENT_COLORS[key]}
                              stroke="#FFFFFF"
                              strokeWidth={2}
                              maxBarSize={24}
                              isAnimationActive={false}
                            >
                              {data.map((point) => (
                                <Cell key={point.label} radius={segmentRadius(topSegment(point) === key)} />
                              ))}
                            </Bar>
                          ))
                        : [
                            <Bar key="converted" dataKey="converted" stackId="a" fill={CONVERTED_COLOR} maxBarSize={24} isAnimationActive={false} />,
                            <Bar
                              key="notConverted"
                              dataKey="notConverted"
                              stackId="a"
                              fill={NOT_CONVERTED_COLOR}
                              maxBarSize={24}
                              radius={[4, 4, 0, 0]}
                              isAnimationActive={false}
                            />,
                          ]}
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={2} divider={<Divider />}>
                  {hasClients ? (
                    <ConversionSegmentMeter
                      label={`Latest · ${periodLabel(periods.latest)}`}
                      counts={countSegments(conversion.clients, 'latest_6m')}
                      ratePct={conversion.latest.ratePct}
                    />
                  ) : (
                    <Meter label={`Latest · ${periodLabel(periods.latest)}`} period={conversion.latest} />
                  )}
                  {hasClients ? (
                    <ConversionSegmentMeter
                      label={`Prior · ${periodLabel(periods.prior)}`}
                      counts={countSegments(conversion.clients, 'prior_6m')}
                      ratePct={conversion.prior.ratePct}
                      muted
                    />
                  ) : (
                    <Meter label={`Prior · ${periodLabel(periods.prior)}`} period={conversion.prior} muted />
                  )}
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography variant="body2" color="text.secondary">
                      All-time
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography sx={{ fontWeight: 700 }}>{pct(conversion.allTime.ratePct)}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {conversion.allTime.converted} of {conversion.allTime.expired}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
            {hasClients ? (
              <Box sx={{ mt: 3 }}>
                <ConversionClientsTable clients={conversion.clients} periods={periods} sourceCreatedDate={sourceCreatedDate} />
              </Box>
            ) : null}
          </>
        ) : (
          <EmptyState title="No conversions recorded" body="No conversion data for this period." />
        )}
      </CardContent>
    </Card>
  );
};
